import {
  Link,
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  SidebarSubmenuItem,
  useSidebarOpenState,
} from '@backstage/core-components';
import { ApiProvider } from '@backstage/core-app-api';
import {
  AppThemeApi,
  appThemeApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { Shortcuts } from '@backstage-community/plugin-shortcuts';
import {
  PlatformFeatureFlagged,
  usePlatformFeatureFlag,
  useUserFeatureValue,
} from '@mb.io/plugin-platform-feature-flag';
import { ODPLayout } from '@mercedes-benz/odp-theme-v2';
import { SIPCentralIcon } from '@mercedes-benz/sip-central';
import CreateComponentIcon from '@mui/icons-material/AddCircleOutline';
import Apps from '@mui/icons-material/Apps';
import HomeIcon from '@mui/icons-material/Home';
import HistoryIcon from '@mui/icons-material/History';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import SecuritySharpIcon from '@mui/icons-material/SecuritySharp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LensBlurIcon from '@mui/icons-material/LensBlur';
import NebulaIcon from './SideBarIcons/NebulaIcon';
import {
  Children,
  createContext,
  isValidElement,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { ApplicationLogo } from './SideBarIcons/ApplicationLogo';
import InsightsIcon from '@mui/icons-material/Insights';
import { PmasIconMui } from '@mercedes-benz/pmas';
import TerminalIcon from '@mui/icons-material/Terminal';
import { AppTitle } from './AppTitle';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import Collapse from '@mui/material/Collapse';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import DyPIcon from './SideBarIcons/DyPIcon';
import CaasIcon from './SideBarIcons/CaasIcon';
import { ThemeSwitchButton } from '../ThemeSwitchButton';
import { DarkLightThemeSwitchButton } from '../DarkLightThemeSwitchButton';
import {
  isSystemThemeChange,
  runSystemThemeChange,
} from '../synchronize-theme/systemThemeChange';
import JourneysIcon from './SideBarIcons/JourneysIcon';
import Button from '@mui/material/Button';
import { NotificationBell } from '@mercedes-benz/backstage-plugin-notification-bell';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ArticleIcon from '@mui/icons-material/Article';
import StorefrontIcon from '@mui/icons-material/Storefront';

interface DropdownContextType {
  openDropdowns: string[];
  toggleDropdown: (id: string) => void;
  setOpenDropdowns: React.Dispatch<React.SetStateAction<string[]>>;
}

const DropdownContext = createContext<DropdownContextType>({
  openDropdowns: [],
  toggleDropdown: () => {},
  setOpenDropdowns: () => {},
});

export const useDropdown = () => useContext(DropdownContext);

const useStyles = makeStyles()(theme => {
  const mainColor = theme.palette.primary.main;

  return {
    sideBarLink: {
      '&:hover': {
        textDecoration: 'none !important',
        color: `${mainColor} !important`,
      },
      '.MuiButton-label div:last-child': {
        width: 'auto',
        marginRight: '1em',
      },
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflowY: 'auto',
      padding: '0',
      margin: '0',
    },
    submenuContainer: {
      backgroundColor: 'rgba(22, 25, 34, 0.1)',
      boxShadow: '0 1px 0 0 rgba(0, 0, 0, 0.1)',
    },
    parentItemOpen: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      '& [class*="BackstageSidebarItem-root"]': {
        backgroundColor: 'transparent !important',
      },
    },
    sidebarItemWithSubmenu: {
      position: 'relative',
      flexShrink: 0,
    },
    submenuItem: {
      '&:first-of-type': {
        paddingTop: '0 !important',
      },
      '&:last-of-type': {
        paddingBottom: '0 !important',
      },
    },
    dropdownItem: {
      margin: '0',
    },
    submenuItemWrapper: {
      textDecoration: 'none !important',
      borderLeft: '3px solid transparent',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      paddingLeft: '12px',
      '& a': {
        textDecoration: 'none !important',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: 'inherit !important',
        fontSize: '0.95em',
        fontWeight: 400,
        '& span': {
          fontSize: '0.95em !important',
          fontWeight: '400 !important',
        },
        '&:active': {
          textDecoration: 'none !important',
          backgroundColor: 'transparent !important',
        },
        '&:hover': {
          color: `${mainColor} !important`,
          '& svg': {
            color: `${mainColor} !important`,
          },
        },
      },
      '& svg': {
        transition: 'color 0.2s',
      },
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    submenuItemActive: {
      '& a': {
        color: `${mainColor} !important`,
      },
      '& a span': {
        color: `${mainColor} !important`,
      },
      '& a svg': {
        color: `${mainColor} !important`,
      },
    },
    dropdownSidebarItemContainer: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      '&:hover': {
        color: `${mainColor} !important`,
        '& svg': {
          color: `${mainColor} !important`,
        },
      },
    },
    dropdownCaret: {
      position: 'absolute',
      right: '-14px',
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      zIndex: 1,
      background: 'none',
      border: 0,
      padding: 0,
      margin: 0,
      pointerEvents: 'none',
      '& svg': {
        fill: 'currentColor',
        color: 'currentColor',
      },
    },
    sidebarStates: {
      '&': {
        '& a': {
          height: '48px',
          minHeight: '48px',
          flexShrink: 0,
          '&:hover': {
            color: `${mainColor} !important`,
            '& svg': {
              color: `${mainColor} !important`,
            },
          },
        },
        '& button': {
          flexShrink: 0,
          '& > span > span': {
            fontSize: '1em',
          },
        },
        '& button[aria-current="page"]': {
          background: 'none',
          color: mainColor,
          '&:hover': {
            background: 'none',
            color: '#FFF',
          },
        },
        '& hr': {
          backgroundColor: '#222',
          margin: '5px 0',
          flexShrink: 0,
          minHeight: '1px',
          height: '1px',
          border: 'none',
        },
        '& > div:empty': {
          flexShrink: 0,
        },
      },
    },
    hideLabelWhenCollapsed: {
      '[class*="BackstageSidebar-drawer-"]:not([class*="BackstageSidebar-drawerOpen-"]) &':
        {
          paddingLeft: 0,
          '& a': {
            justifyContent: 'center',
          },
          '& a span': {
            display: 'none !important',
          },
        },
    },

    submenuCollapsed: {
      paddingLeft: 0,
      '& a': {
        justifyContent: 'center',
      },
      '& a span': {
        display: 'none !important',
      },
    },
  };
});

const useSidebarLogoStyles = makeStyles()({
  root: {
    width: '100%',
    height: 3 * sidebarConfig.logoHeight,
    minHeight: '96px',
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'center',
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
  },
  logoContainer: {
    '[class*="BackstageSidebar-drawer-"]:not([class*="BackstageSidebar-drawerOpen-"]) &':
      {
        '& svg': {
          width: '40px !important',
          height: '40px !important',
        },
      },
  },
});

const SidebarLogo = () => {
  const { classes } = useSidebarLogoStyles();

  return (
    <div className={`${classes.root} ${classes.logoContainer}`}>
      <Link component={NavLink} to="/" underline="none">
        <ApplicationLogo />
      </Link>
    </div>
  );
};

const FlaggedShortcuts = () => (
  <PlatformFeatureFlagged feature="shortcuts">
    <Shortcuts />
  </PlatformFeatureFlagged>
);

const useShouldOpenDropdown = (testId: string, children: React.ReactNode) => {
  const routerLocation = useLocation();
  const { setOpenDropdowns } = useDropdown();

  useEffect(() => {
    const childPaths = Children.toArray(children)
      .map(child => {
        if (isValidElement(child) && child.props.children) {
          const submenuItem = Children.toArray(child.props.children)[0];
          if (isValidElement(submenuItem) && submenuItem.props.to) {
            return submenuItem.props.to;
          }
        }
        return null;
      })
      .filter((path): path is string => Boolean(path));

    if (
      childPaths.some(path => {
        const pluginRoot = `/${path.split('/').filter(Boolean)[0] ?? ''}`;
        return (
          routerLocation.pathname === path ||
          routerLocation.pathname === pluginRoot ||
          routerLocation.pathname.startsWith(`${pluginRoot}/`)
        );
      })
    ) {
      setOpenDropdowns(prev =>
        prev.includes(testId) ? prev : [...prev, testId],
      );
    }
  }, [routerLocation.pathname, children, testId, setOpenDropdowns]);
};

interface DropdownSidebarItemProps {
  icon: React.ComponentType<any>;
  text: string;
  className?: string;
  testId: string;
  children: React.ReactNode;
}

const DropdownSidebarItem = ({
  icon: Icon,
  text,
  className,
  testId,
  children,
}: DropdownSidebarItemProps) => {
  const { openDropdowns, toggleDropdown } = useDropdown();
  const { classes } = useStyles();

  const isOpen = openDropdowns.includes(testId);

  useShouldOpenDropdown(testId, children);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleDropdown(testId);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      toggleDropdown(testId);
    }
  };

  return (
    <div className={classes.sidebarItemWithSubmenu}>
      <div
        className={`${classes.dropdownSidebarItemContainer} ${isOpen ? classes.parentItemOpen : ''}`}
      >
        <SidebarItem
          icon={Icon as never}
          text={text}
          onClick={handleClick}
          data-testid={testId}
          className={className}
        />
        <Button
          className={classes.dropdownCaret}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          type="button"
          aria-label={`Toggle ${text} menu`}
        >
          {isOpen ? (
            <ArrowDropDownIcon fontSize="small" />
          ) : (
            <ArrowRightIcon fontSize="small" />
          )}
        </Button>
      </div>
      <Collapse in={isOpen} timeout={300} collapsedSize={0}>
        <div className={classes.submenuContainer}>{children}</div>
      </Collapse>
    </div>
  );
};

interface StyledSubmenuItemProps {
  className?: string;
  children: React.ReactNode;
}

const StyledSubmenuItem = ({ className, children }: StyledSubmenuItemProps) => {
  const { classes } = useStyles();
  const location = useLocation();
  const { isOpen } = useSidebarOpenState();

  const to = useMemo(() => {
    if (!isValidElement(children)) {
      return undefined;
    }

    const rawTo = (children.props as { to?: unknown })?.to;
    if (typeof rawTo !== 'string' || rawTo.length === 0) {
      return undefined;
    }

    return rawTo.startsWith('/') ? rawTo : `/${rawTo}`;
  }, [children]);

  const isActive = Boolean(
    to &&
    (() => {
      if (location.pathname === to) return true;
      if (to === '/') return false;
      if (location.pathname.startsWith(`${to}/`)) return true;

      const pluginRoot = `/${to.split('/').filter(Boolean)[0] ?? ''}`;
      return (
        pluginRoot !== '/' &&
        (location.pathname === pluginRoot ||
          location.pathname.startsWith(`${pluginRoot}/`))
      );
    })(),
  );

  return (
    <div
      className={`${classes.submenuItemWrapper} ${classes.submenuItem} ${classes.dropdownItem} ${isOpen ? '' : classes.submenuCollapsed} ${isActive ? classes.submenuItemActive : ''} ${className ?? ''}`}
    >
      {children}
    </div>
  );
};

// Custom hook for Safari detection
const useIsSafari = () => {
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    setIsSafari(
      /^((?!chrome|android).)*safari/i.test(
        globalThis.navigator?.userAgent ?? '',
      ),
    );
  }, []);

  return isSafari;
};

interface NavItemProps {
  to: string;
  icon: React.ComponentType<any>;
  text: string;
  testId: string;
  onClick?: () => void;
  className?: string;
  title?: string;
}

const NavItem = ({
  to,
  icon,
  text,
  testId,
  onClick,
  className,
  title,
}: NavItemProps) => {
  const { classes } = useStyles();
  const { setOpenDropdowns } = useDropdown();

  const handleClick = () => {
    setOpenDropdowns([]);
    onClick?.();
  };

  return (
    <SidebarItem
      icon={icon as never}
      to={to}
      text={text}
      data-testid={testId}
      className={`${classes.sideBarLink} ${className ?? ''}`}
      onClick={handleClick}
      title={title}
    />
  );
};

export const ClassicRoot = ({ children }: PropsWithChildren<{}>) => {
  const { classes } = useStyles();
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([]);
  const isSafari = useIsSafari();
  const toggleDropdown = useCallback((id: string) => {
    setOpenDropdowns(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  }, []);

  const dropdownContextValue = useMemo(
    () => ({
      openDropdowns,
      toggleDropdown,
      setOpenDropdowns,
    }),
    [openDropdowns, toggleDropdown, setOpenDropdowns],
  );

  const sidebarContent = (
    <>
      <SidebarLogo />
      <AppTitle />
      <NavItem
        to="/search"
        icon={SearchIcon}
        text="Search"
        testId="search-button"
      />
      <SidebarDivider />
      {/* Global nav, not org-specific */}
      <NavItem icon={HomeIcon} to="/home" text="Home" testId="home-button" />
      <NavItem
        title="Journeys"
        icon={JourneysIcon}
        to="/journeys"
        text="Journeys"
        testId="journeys-button"
      />
      <NavItem
        title="Catalog"
        icon={Apps}
        to="/catalog"
        text="Catalog"
        testId="catalog-button"
      />
      <NavItem
        icon={ArticleIcon}
        to="/docs"
        text="Documentation"
        testId="documentation-button"
      />
      <NavItem
        icon={SchoolRoundedIcon}
        to="/academy"
        text="Academy"
        testId="academy-button"
      />
      <NavItem
        icon={HomeRepairServiceIcon}
        to="/toolbox"
        text="Toolbox"
        testId="explore-button"
      />
      <NavItem
        icon={CreateComponentIcon}
        to="/create"
        text="Templates"
        testId="create-button"
      />
      <NavItem
        icon={StorefrontIcon}
        to="/tdms-next"
        text="Shop"
        testId="shop-button"
      />
      <SidebarDivider />
      <NavItem
        icon={AutoAwesomeIcon}
        to="/aiecosystem"
        text="AI Ecosystem"
        testId="aiecosystem-button"
      />
      <NavItem
        icon={LensBlurIcon}
        to="/aievaluation"
        text="AI Evaluation"
        testId="aievaluation-button"
      />
      <NavItem
        icon={InsightsIcon}
        to="/insights-hub"
        text="Insights Hub"
        testId="insights-hub-button"
      />
      <DropdownSidebarItem
        icon={NebulaIcon}
        text="Nebula"
        className={classes.sideBarLink}
        testId="nebula-button"
      >
        <StyledSubmenuItem>
          <SidebarSubmenuItem
            title="Monitoring Hub"
            to="/monitoring-hub"
            icon={PmasIconMui as never}
            data-testid="monitoring-hub-button"
          />
        </StyledSubmenuItem>
        <StyledSubmenuItem>
          <SidebarSubmenuItem
            title="Asset Explorer"
            to="/asset-explorer"
            icon={CloudQueueIcon as never}
            data-testid="asset-explorer-button"
          />
        </StyledSubmenuItem>
        <StyledSubmenuItem>
          <SidebarSubmenuItem
            title="CLM Jobs Dashboard"
            to="/clm-jobs-dashboard"
            icon={TranslateIcon as never}
            data-testid="clm-jobs-dashboard-button"
          />
        </StyledSubmenuItem>
        <PlatformFeatureFlagged feature="prReviewDashboard">
          <StyledSubmenuItem>
            <SidebarSubmenuItem
              title="PR Review Dashboard"
              to="/pr-review-dashboard"
              icon={RateReviewOutlinedIcon as never}
              data-testid="pr-review-dashboard-button"
            />
          </StyledSubmenuItem>
        </PlatformFeatureFlagged>
        <PlatformFeatureFlagged feature="auditLog">
          <StyledSubmenuItem>
            <SidebarSubmenuItem
              title="Audit Log"
              to="/audit-log"
              icon={HistoryIcon as never}
              data-testid="audit-log-button"
            />
          </StyledSubmenuItem>
        </PlatformFeatureFlagged>
      </DropdownSidebarItem>
      <DropdownSidebarItem
        icon={WbSunnyIcon}
        text="Helios"
        className={classes.sideBarLink}
        testId="helios-button"
      >
        <StyledSubmenuItem>
          <SidebarSubmenuItem
            icon={SIPCentralIcon}
            to="/sip-central"
            title="HELIOS Central"
            data-testid="sip-central-button"
          />
        </StyledSubmenuItem>
        <StyledSubmenuItem>
          <SidebarSubmenuItem
            icon={SecuritySharpIcon as never}
            to="/seccomp"
            title="Compliance"
            data-testid="seccomp-button"
          />
        </StyledSubmenuItem>
      </DropdownSidebarItem>
      <DropdownSidebarItem
        icon={DyPIcon}
        text="Dynamic Platforms"
        className={classes.sideBarLink}
        testId="dyp-button"
      >
        <StyledSubmenuItem>
          <SidebarSubmenuItem
            icon={CaasIcon}
            to="/caas"
            title="CaaS"
            data-testid="caas-button"
          />
        </StyledSubmenuItem>
        <StyledSubmenuItem>
          <SidebarSubmenuItem to="/adp" title="ADP" data-testid="adp-button" />
        </StyledSubmenuItem>
      </DropdownSidebarItem>
      {/* End global nav */}
      <FlaggedShortcuts />
      <SidebarSpace />
      <SidebarDivider />
      <PlatformFeatureFlagged feature="odpDesignV2">
        <ThemeSwitchButton />
      </PlatformFeatureFlagged>
      <NavItem
        icon={SettingsOutlinedIcon}
        to="/settings"
        text="Settings"
        testId="settings-button"
      />
      <PlatformFeatureFlagged feature="themeSwitcherSidebar">
        <DarkLightThemeSwitchButton />
      </PlatformFeatureFlagged>
      <RequirePermission
        permission={devToolsAdministerPermission}
        errorPage={<></>}
      >
        <NavItem
          icon={TerminalIcon}
          to="/devtools"
          text="DevTools"
          testId="devtools-button"
        />
      </RequirePermission>
    </>
  );

  return (
    <SidebarPage>
      <Sidebar disableExpandOnHover>
        <DropdownContext.Provider value={dropdownContextValue}>
          {isSafari ? (
            <Box className={classes.container}>
              <div className={classes.sidebarStates}>{sidebarContent}</div>
            </Box>
          ) : (
            <div
              className={classes.sidebarStates}
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {sidebarContent}
            </div>
          )}
        </DropdownContext.Provider>
      </Sidebar>
      <Box sx={{ width: '100%', minWidth: 0 }}>{children}</Box>
    </SidebarPage>
  );
};

const isClassicTheme = (id: string | undefined) =>
  id === 'backstage-theme-dark' || id === 'backstage-theme-light';

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const location = useLocation();
  const navigate = useNavigate();
  const appThemeApi = useApi(appThemeApiRef);
  const odpDesignV2Flag = usePlatformFeatureFlag('odpDesignV2');
  const flagLoaded = odpDesignV2Flag !== undefined;
  const userToggle = useUserFeatureValue({
    feature: 'odpDesignV2',
    defaultValue: odpDesignV2Flag?.defaultToggleValue ?? false,
  });
  // The flag is toggleable per-user: available=false globally but individual
  // users can opt-in. Check the user toggle when toggleable, else check available.
  const isV2Enabled = odpDesignV2Flag?.toggleable
    ? userToggle
    : odpDesignV2Flag?.available === true;
  const [themeId, setThemeId] = useState<string | undefined>(
    appThemeApi.getActiveThemeId(),
  );

  const prevThemeIdRef = useRef<string | undefined>(themeId);

  // If the feature flag is explicitly disabled but a v2 theme is active, revert to classic.
  // Do NOT revert while the flag is still loading (undefined).
  useEffect(() => {
    if (flagLoaded && !isV2Enabled && themeId?.startsWith('odp-v2-')) {
      runSystemThemeChange(() =>
        appThemeApi.setActiveThemeId('backstage-theme-dark'),
      );
    }
  }, [flagLoaded, isV2Enabled, themeId, appThemeApi]);

  useEffect(() => {
    const subscription = appThemeApi.activeThemeId$().subscribe(id => {
      const prevId = prevThemeIdRef.current;
      const prevIsClassic = isClassicTheme(prevId);
      const nextIsClassic = isClassicTheme(id);

      // Navigate to home when the user switches layout type (classic ↔ v2).
      // App-initiated changes (first-load hydration, backend preference sync,
      // feature-flag revert) must not clobber the deep link the user arrived on.
      if (
        !isSystemThemeChange() &&
        prevId !== undefined &&
        prevId !== id &&
        prevIsClassic !== nextIsClassic
      ) {
        if (!isV2Enabled && !nextIsClassic) return;
        navigate('/home');
      }

      prevThemeIdRef.current = id;
      setThemeId(id);
    });
    return () => subscription.unsubscribe();
  }, [appThemeApi, navigate, isV2Enabled]);

  const isV2Theme = themeId?.startsWith('odp-v2-') ?? false;

  // Show ODPLayout when the active theme is v2 AND the feature flag either
  // hasn't loaded yet (trust the theme choice) or is explicitly enabled.
  // This avoids flicker back to ClassicRoot when the tree remounts after a
  // theme-provider swap — the flag context resets to undefined on remount.
  if (isV2Theme && (!flagLoaded || isV2Enabled)) {
    const isHome = location.pathname === '/home' || location.pathname === '/';
    return (
      <ODPLayout
        transparentHeader={isHome}
        headerActions={<NotificationBell />}
      >
        {children}
      </ODPLayout>
    );
  }

  const filteredThemeApi: AppThemeApi = {
    getInstalledThemes: () =>
      appThemeApi.getInstalledThemes().filter(t => !t.id.startsWith('odp-v2-')),
    activeThemeId$: () => appThemeApi.activeThemeId$(),
    getActiveThemeId: () => appThemeApi.getActiveThemeId(),
    setActiveThemeId: (id?: string) => appThemeApi.setActiveThemeId(id),
  };

  const filteredThemeApiHolder = {
    get<T>(ref: { id: string }): T | undefined {
      if (ref.id === appThemeApiRef.id) {
        return filteredThemeApi as unknown as T;
      }
      return undefined;
    },
  };

  return (
    <ApiProvider apis={filteredThemeApiHolder}>
      <ClassicRoot>{children}</ClassicRoot>
    </ApiProvider>
  );
};
